var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../field-note/field-note';
import '../icon/icons/warning-diamond-filled';
import '../icon/icons/warning-triangle-filled';
import '../skeleton/skeleton';
import styles from './radio-field.styles';
import { SlotController } from '../../controllers/slot-controller';
/**
 * @slot default - The component content
 * @slot error - error slot
 * @slot field-note - field-note slot
 * @slot warning - warningNote slot
 *
 * @fires bl-radio-change - Emitted when radio field is selected.
 *
 * @csspart base - The component's base wrapper.
 * @csspart container - The component's container wrapper.
 * @csspart input - The component's input element.
 * @csspart custom-wrapper - The component's custom wrapper field.
 * @csspart custom-radio - The component's custom radio field.
 * @csspart part-label - The component's label element.
 * @csspart part-default- The component's default slot.
 * @csspart part-required - The component's label required wrapper
 * @csspart loader - The component's skeleton loader element.
 * @csspart field-note-loader - The component's skeleton field note loader element.
 * @csspart field-note-skeleton - The component's skeleton field note skeleton element.
 * @csspart field-note-wrapper - The component's field-note wrapper.
 * @csspart part-field-note - The component's field-note slot.
 * @csspart part-warning - The component's warning slot.
 * @csspart warning-icon - The component's warning icon.
 * @csspart part-error - The component's error slot.
 * @csspart error-icon - The component's error icon.
 */
export class BlocksRadioField extends BlocksElement {
    constructor() {
        super(...arguments);
        /**
         * readOnly attribute
         */
        this.readOnly = false;
        /**
         * Checked property
         */
        this.checked = false;
        /**
         * isInline property
         * When true, this property overrides the default flex behavior, aligning the element inline instead of filling the container width.
         */
        this.isInline = false;
        /**
         * Name or Title for the warning icon
         * meant for accessibility purposes only
         */
        this.warningIconTitle = 'warning icon';
        /**
         * Name or Title for the error icon
         * meant for accessibility purposes only
         */
        this.errorIconTitle = 'error icon';
        /**
         * Indicates that the radio-field is in pressed state.
         */
        this.radioPressed = false;
        this.slots = new SlotController(this, null, 'error', 'field-note', 'warning');
        this.internals_ = this.attachInternals?.();
        this.type = 'radio';
        /**
         * Remove checked
         * 1) Remove checked property from all items and set tabindex to -1
         */
        this.unCheckAll = () => {
            /* 1 */
            this.getAllNamedSiblings.forEach((element) => {
                if (!element.readOnly) {
                    element.checked = false;
                    element.radioFieldParentWrapper?.setAttribute('tabindex', '-1');
                }
            });
        };
        /**
         * Toggle active state of primary nav item
         * 1) Remove isActive state from all sibling elements
         * 2) Toggle active state of element selected
         */
        this.handleRadioSelection = () => {
            this.unCheckAll();
            this.checked = true; /* 2 */
            this.dispatchRadioChangeEvent();
        };
        /**
         * Handle keydown
         * 1) To prevent default click in readonly state on pressing SPACE
         */
        this.handleKeyDown = (e) => {
            if (e.key === KEYS.LEFT_ARROW || e.key === KEYS.UP_ARROW) {
                this.activatePreviousRadioButton();
            }
            else if (e.key === KEYS.RIGHT_ARROW || e.key === KEYS.DOWN_ARROW) {
                this.activateNextRadioButton();
            }
            if (e.key === KEYS.ENTER && !this.radioItemsChecked && !this.readOnly && !this.checked) {
                this.handleRadioSelection();
            }
            if (e.key === KEYS.SPACE && !this.readOnly) {
                this.radioPressed = true;
            }
            if (this.readOnly && e.key === KEYS.SPACE) {
                e.preventDefault();
            }
        };
    }
    static { this.formAssociated = true; }
    static { this.styles = styles; }
    get hasFieldNoteSlot() {
        return this.slots.hasSlotted('field-note');
    }
    get hasErrorSlot() {
        return this.slots.hasSlotted('error');
    }
    get hasWarningSlot() {
        return this.slots.hasSlotted('warning');
    }
    /**
     * Query all radio button with same name
     */
    get getAllNamedSiblings() {
        return Array.from(this.getRootNode().querySelectorAll(`cat-radio-field[name="${this.name}"]`));
    }
    /**
     * Query for any radio group item checked
     */
    get radioItemsChecked() {
        return Array.from(this.getRootNode().querySelectorAll(`cat-radio-field[name="${this.name}"]`)).some((el) => el.checked === true);
    }
    /**
     * Associates the control with a form element
     */
    get form() {
        return this.internals_?.form;
    }
    /**
     * Autogenerate
     */
    connectedCallback() {
        super.connectedCallback();
        this.fieldId = this.fieldId || nanoid();
    }
    firstUpdated() {
        this.updateTabIndex();
    }
    updated(_changedProperties) {
        if (_changedProperties.has('value') || _changedProperties.has('checked')) {
            this.internals_?.setFormValue(this.checked ? (this.value ?? null) : null);
        }
        if (_changedProperties.has('checked')) {
            this.updateTabIndex();
        }
        if (_changedProperties.has('autoFocus') && this.autoFocus) {
            this.focus();
        }
    }
    /**
     * Set the tabindex value to 0 for the checked radio field or no radio item is checked
     * otherwise set tabindex -1
     * 1) radioItemsChecked - In case the user is updating the checked status to false and none of the radio group item is checked then make the tabindex to 0 so the focus will be available for first clickable radio
     */
    async updateTabIndex() {
        await this.updateComplete;
        this.setTabIndexForRadioInputs();
    }
    /**
     * Sets the tabindex attribute for radio inputs within the named siblings.
     *
     * This function iterates through all named siblings and sets the tabindex attribute
     * of their corresponding radio inputs to either '0' (focusable) or '-1' (not focusable).
     * The tabindex is set to '0' only for the radio input at the index matching the first enabled radio button.
     *
     */
    setTabIndexForRadioInputs() {
        const firstEnabledRadioIndex = this.findEnabledRadioIndex();
        if (firstEnabledRadioIndex !== -1) {
            this.getAllNamedSiblings.forEach((element, index) => {
                element.radioFieldParentWrapper?.setAttribute('tabindex', index === firstEnabledRadioIndex ? '0' : '-1');
            });
        }
    }
    /**
     * Finds the index of the first enabled radio button within the named siblings.
     * @private
     * @returns {number} The index of the first enabled radio button, or -1 if none are found.
     */
    findEnabledRadioIndex() {
        const nonDisabledCheckedRadio = this.getAllNamedSiblings.findIndex((el) => !el.disabled && el.checked);
        const firstNonDisabledRadio = this.getAllNamedSiblings.findIndex((el) => !el.disabled);
        return nonDisabledCheckedRadio > -1 ? nonDisabledCheckedRadio : firstNonDisabledRadio;
    }
    /**
     * Dispatch radio change event
     */
    dispatchRadioChangeEvent() {
        this.dispatch({ eventName: 'bl-radio-change', detailObj: { name: this.name, value: this.value } });
    }
    /**
     * Handle Keyup
     * 1) To have bg-color on input radio on pressed state
     */
    handleKeyUp(e) {
        if (e.key === KEYS.SPACE) {
            this.radioPressed = false;
            if (!this.radioItemsChecked && !this.readOnly && !this.checked) {
                this.handleRadioSelection();
            }
        }
    }
    focus() {
        this.radioFieldParentWrapper?.focus();
    }
    activatePreviousRadioButton() {
        /* 1 */
        const index = this.findPreviousRadioButtonIndex();
        const previousElement = this.getAllNamedSiblings[index];
        if (previousElement) {
            const radioElement = previousElement.radioFieldParentWrapper;
            radioElement.setAttribute('tabindex', '0');
            radioElement.focus();
            if (!previousElement.readOnly) {
                this.unCheckAll();
                previousElement.checked = true;
                this.dispatchRadioChangeEvent();
            }
        }
    }
    /**
     * 1) Find current active radio button and move the active status to previous button
     * 2) If no more previous radio button means move active status to last radio button
     * 3) Check whether previous radio is disabled or not if it is disabled move active state to previously enabled radio
     */
    findPreviousRadioButtonIndex() {
        const radioGroupLength = this.getAllNamedSiblings.length;
        let index = this.getAllNamedSiblings.indexOf(this); /**1 */
        index = index - 1;
        if (index < 0) {
            /**2 */
            index = radioGroupLength - 1;
        }
        /**3 */
        if (this.getAllNamedSiblings[index]) {
            while (this.getAllNamedSiblings[index].disabled) {
                if (index === 0) {
                    index = radioGroupLength - 1;
                }
                else {
                    index = index - 1;
                }
            }
        }
        return index;
    }
    activateNextRadioButton() {
        const index = this.findNextRadioButtonIndex();
        const nextElement = this.getAllNamedSiblings[index];
        if (nextElement) {
            const catRadioElement = nextElement.radioFieldParentWrapper;
            catRadioElement.setAttribute('tabindex', '0');
            catRadioElement.focus();
            if (!nextElement.readOnly) {
                this.unCheckAll();
                nextElement.checked = true;
                this.dispatchRadioChangeEvent();
            }
        }
    }
    /**
     * 1) Find current active radio button and move the active status to next radio button if current active
     * 2) Check whether next radio is disabled or not if it is disabled move active state to next enabled radio button
     */
    findNextRadioButtonIndex() {
        const radioGroupLength = this.getAllNamedSiblings.length;
        /**1 */
        let index = this.getAllNamedSiblings.indexOf(this);
        if (radioGroupLength > index) {
            index = index + 1;
        }
        if (radioGroupLength == index) {
            index = 0;
        }
        /**2 */
        if (this.getAllNamedSiblings[index]) {
            while (this.getAllNamedSiblings[index].disabled) {
                if (radioGroupLength > index) {
                    index = index + 1;
                }
                if (radioGroupLength === index) {
                    index = 0;
                }
            }
        }
        return index;
    }
    /**
     * 1) To prevent toggling of readonly radio field in browse/virtual mode of screen reader
     */
    handleOnChange() {
        if (this.readOnly || this.disabled) {
            this.shadowRoot.querySelector('input').checked = this.checked; /* 1 */
        }
    }
    hasFieldNote() {
        return (this.fieldNote ||
            this.errorNote ||
            this.warningNote ||
            this.hasFieldNoteSlot ||
            this.hasErrorSlot ||
            this.hasWarningSlot);
    }
    /**
     * Display error note from slot or from errorNote property
     */
    getErrorNote() {
        return html `
      <cat-icon-warning-diamond-filled
        part="error-icon"
        exportparts=${this.getExportParts('warning-diamond-filled', 'icon')}
        color="error"
        .iconTitle=${this.errorIconTitle}
        class="cat-c-radio-field__icon"
      ></cat-icon-warning-diamond-filled>
      <cat-field-note
        part="field-note-wrapper"
        exportparts=${this.getExportParts('field-note')}
        class="cat-c-radio--fieldnote cat-c-radio-field__error-note"
        ?isDisabled=${this.disabled}
        .status=${'error'}
      >
        <slot part="part-error" name="error">${this.errorNote}</slot>
      </cat-field-note>
    `;
    }
    /**
     * Display warning note from slot or from warningNote property
     */
    getWarningNote() {
        return html `
      <cat-icon-warning-triangle-filled
        part="warning-icon"
        exportparts=${this.getExportParts('warning-triangle-filled', 'icon')}
        color="knockout-brand"
        .iconTitle=${this.warningIconTitle}
        class="cat-c-radio-field__icon"
      ></cat-icon-warning-triangle-filled>
      <cat-field-note
        part="field-note-wrapper"
        exportparts=${this.getExportParts('field-note')}
        class="cat-c-radio--fieldnote cat-c-radio-field__warning-note"
        ?isDisabled=${this.disabled}
        .status=${'warning'}
      >
        <slot part="part-warning" name="warning">${this.warningNote}</slot>
      </cat-field-note>
    `;
    }
    getFieldNote() {
        return html `<cat-field-note
      part="field-note-wrapper"
      exportparts=${this.getExportParts('field-note')}
      class="cat-c-radio--fieldnote cat-c-radio-field__field-note"
      ?isDisabled=${this.disabled}
      .status=${this.status}
    >
      <slot part="part-field-note" name="field-note">${this.fieldNote}</slot>
    </cat-field-note> `;
    }
    /**
     * To render loading variant
     */
    renderLoading(componentClassNames) {
        return html ` <div part="base" class="${componentClassNames}">
      <div part="container" class="cat-c-radio-field__container">
        <div part="input" class="cat-c-radio__input"></div>
        <span part="custom-radio" class="cat-c-radio__custom-radio"></span>
        <cat-skeleton
          part="loader"
          exportparts=${this.getExportParts('skeleton')}
          class="cat-c-radio-field-loading"
          width="100px"
          size="sm"
        ></cat-skeleton>
      </div>
      ${this.hasFieldNote()
            ? html `
            <div part="field-note-loader" class="cat-c-radio-field__fieldnote-loader">
              <cat-skeleton
                part="field-note-skeleton"
                arialabel=""
                exportparts=${this.getExportParts('skeleton-field-note', 'skeleton')}
                size="sm"
                width="100px"
              ></cat-skeleton>
            </div>
          `
            : nothing}
    </div>`;
    }
    render() {
        const componentClassNames = this.componentClassNames('cat-c-radio-field', {
            'cat-is-disabled': this.disabled,
            'cat-is-active': this.checked === true,
            'cat-is-error': this.status === 'error',
            'cat-is-readonly': this.readOnly,
            'cat-is-loading': this.isLoading,
            'cat-is-inline': this.isInline === true,
            'cat-c-radio--hidden-label': this.hideLabel,
            'cat-c-radio-readonly': this.readOnly,
            'cat-c-radio-field--has-error': (this.errorNote || this.hasErrorSlot) && this.status === 'error',
            'cat-c-radio-field--has-warning': (this.warningNote || this.hasWarningSlot) && this.status === 'warning',
            'cat-c-radio-field--has-field-note': (this.fieldNote || this.hasFieldNoteSlot) && this.status !== 'error' && this.status !== 'warning'
        });
        if (this.isLoading) {
            return this.renderLoading(componentClassNames);
        }
        return html `
      <div
        part="base"
        class="${componentClassNames}"
        @click=${!this.checked && !this.disabled && !this.readOnly ? this.handleRadioSelection : nothing}
        role="radio"
        aria-checked=${this.checked}
        tabindex=${this.disabled ? '-1' : '0'}
        aria-required="${ifDefined(!this.notation ? (this.required ? 'true' : undefined) : undefined)}"
        aria-disabled="${ifDefined(this.disabled || this.readOnly)}"
        @keydown=${this.handleKeyDown}
        @keyup=${this.handleKeyUp}
      >
        <div
          part="container"
          class=${this.radioPressed ? 'cat-c-radio-field__container cat-c-radio-field__container-active' : 'cat-c-radio-field__container'}
        >
          <input
            part="input"
            tabindex="-1"
            id="${ifDefined(this.fieldId)}"
            type="radio"
            name="${ifDefined(this.name)}"
            class="cat-c-radio__input"
            .value="${this.value ?? ''}"
            .checked="${this.checked}"
            ?disabled="${this.disabled}"
            @change=${this.handleOnChange}
          />
          <!-- This span is added because the input radio class is not taking scss changes. So, applied scss to this new span class -->
          <span part="custom-wrapper" class="cat-c-radio__custom-wrapper"></span>
          <span part="custom-radio" class="cat-c-radio__custom-radio"></span>
          <label part="part-label" class="cat-c-radio-field__label" for="${ifDefined(this.fieldId)}">
            <slot part="part-default">${this.label}</slot>${this.required
            ? html `<span part="part-required" class="cat-c-radio-field__required">${this.notation}</span>`
            : nothing}</label
          >
        </div>
        <div class="cat-c-radio-field__fieldnote-container">
          <!--- field note -->
          ${this.getFieldNote()}
          <!--- error note -->
          ${this.getErrorNote()}
          <!--- warning note -->
          ${this.getWarningNote()}
        </div>
      </div>
    `;
    }
}
__decorate([
    property({ type: String })
], BlocksRadioField.prototype, "status", void 0);
__decorate([
    property({ type: String })
], BlocksRadioField.prototype, "errorNote", void 0);
__decorate([
    property({ type: String })
], BlocksRadioField.prototype, "warningNote", void 0);
__decorate([
    property({ type: String })
], BlocksRadioField.prototype, "fieldNote", void 0);
__decorate([
    property({ type: Boolean })
], BlocksRadioField.prototype, "readOnly", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BlocksRadioField.prototype, "disabled", void 0);
__decorate([
    property({ type: String })
], BlocksRadioField.prototype, "value", void 0);
__decorate([
    property({ type: String })
], BlocksRadioField.prototype, "fieldId", void 0);
__decorate([
    property({ type: String, reflect: true })
], BlocksRadioField.prototype, "name", void 0);
__decorate([
    property({ type: String })
], BlocksRadioField.prototype, "label", void 0);
__decorate([
    property({ type: String })
], BlocksRadioField.prototype, "notation", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BlocksRadioField.prototype, "checked", void 0);
__decorate([
    property({ type: Boolean })
], BlocksRadioField.prototype, "required", void 0);
__decorate([
    property({ type: Boolean })
], BlocksRadioField.prototype, "hideLabel", void 0);
__decorate([
    property({ type: Boolean })
], BlocksRadioField.prototype, "isInline", void 0);
__decorate([
    property({ type: Boolean })
], BlocksRadioField.prototype, "isLoading", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BlocksRadioField.prototype, "autoFocus", void 0);
__decorate([
    property({ type: String })
], BlocksRadioField.prototype, "warningIconTitle", void 0);
__decorate([
    property({ type: String })
], BlocksRadioField.prototype, "errorIconTitle", void 0);
__decorate([
    state()
], BlocksRadioField.prototype, "radioPressed", void 0);
__decorate([
    query('.cat-c-radio-field')
], BlocksRadioField.prototype, "radioFieldParentWrapper", void 0);
if (customElements.get('cat-radio-field') === undefined) {
    customElements.define('cat-radio-field', BlocksRadioField);
}
